import { CheckCircleIcon, PlusIcon } from "@heroicons/react/20/solid";
import { ComponentColor } from "../../../helpers/constants/types";
import { VacancyLesson } from "../../../models/schedule/lesson";
import { Teacher } from "../../../models/teacher/teacher";
import { SubstituteSchedulerLesson } from "../../scheduler/SubstituteScheduler";
import { ContentFactory } from "../content-factory/ContentFactory";
import { IContent } from "../content-factory/IContent";
import { HighlightTeacher } from "./highlightTeacherDecorator";
import {
  ISchedulerViewMode,
  SchedulerViewConfiguration,
} from "./ISchedulerViewMode";

/**
 * Decorates the viewModeWeek class with the possibility to display schedules of subtitute teachers
 */
export class ViewModeSubstituteWeek
  implements ISchedulerViewMode<SubstituteSchedulerLesson>
{
  private isDefault = !this.selectedSubstitute;
  private viewMode: ISchedulerViewMode<SubstituteSchedulerLesson>;

  constructor(
    viewMode: ISchedulerViewMode<SubstituteSchedulerLesson>,
    private selectedSubstitute?: Teacher,
  ) {
    this.viewMode = selectedSubstitute
      ? new HighlightTeacher(viewMode, selectedSubstitute.id)
      : viewMode;
  }

  getWeekday(
    startDate: Date,
    day: number,
  ): { id: number; name: string; shortName: string } {
    return this.viewMode.getWeekday(startDate, day);
  }

  toggleActive(
    day: number,
    duration: number,
    lessons: SubstituteSchedulerLesson[][],
  ): SubstituteSchedulerLesson[] {
    const changedLessons = this.viewMode.toggleActive(
      day,
      duration,
      this.isDefault ? lessons : this.modifyLessons(lessons),
    );

    return this.modifyReturnLessons(
      lessons,
      changedLessons,
      !changedLessons[0]?.selectedSubstitute,
    );
  }

  setDay(
    day: number,
    value: boolean,
    lessons: SubstituteSchedulerLesson[][],
  ): SubstituteSchedulerLesson[] {
    const changedLessons = this.viewMode.setDay(
      day,
      value,
      this.isDefault ? lessons : this.modifyLessons(lessons),
    );

    return this.modifyReturnLessons(lessons, changedLessons, value);
  }

  displayLessons(
    startDate: Date,
    lessons: SubstituteSchedulerLesson[][],
    page = 0,
  ): SubstituteSchedulerLesson[][] {
    let lessonSlice = this.viewMode.displayLessons(
      startDate,
      lessons,
      page,
    ) as SubstituteSchedulerLesson[][];

    if (this.isDefault) {
      return lessonSlice;
    }

    lessonSlice = this.modifyLessons(lessonSlice);

    return lessonSlice;
  }

  hasPage(page: number, lessons: SubstituteSchedulerLesson[][]): boolean {
    return this.viewMode.hasPage(page, lessons);
  }

  getLessonContent(
    lesson: SubstituteSchedulerLesson,
    viewConfiguration: SchedulerViewConfiguration,
  ): IContent | null {
    if (
      lesson.teacherApplications?.some(
        (application) => application.id === this.selectedSubstitute?.id,
      )
    ) {
      return ContentFactory.createContainer().withChildren([
        ContentFactory.createIcon(<CheckCircleIcon />),
        ContentFactory.createText("Beworben"),
      ]).withStyle("flex items-center space-x-1");
    } else if (!lesson.canActivate && !lesson.selectedSubstitute) {
      return null;
    } else if (lesson.selectedSubstitute) {
      return ContentFactory.createContainer()
        .withChildren([
          ContentFactory.createText(
            viewConfiguration.size === "small"
              ? (lesson.name ?? "")[0]
              : lesson.name ?? "",
          ).withTestAttribute("content-text"),
          ContentFactory.createCircle(lesson.selectedSubstitute.initials)
            .withTitle(lesson.selectedSubstitute.fullName)
            .withStyle("text-white"),
        ])
        .withStyle("flex justify-between w-full")
        .withTestAttribute("content-container");
    } else if (!this.isDefault && lesson.teacherApplications?.length) {
      return ContentFactory.createContainer()
        .withChildren([
          ContentFactory.createIcon(<PlusIcon />).withStyle(
            "text-th-primary-900",
          ),
          ContentFactory.createCircle(
            `${lesson.teacherApplications.length}`,
          ).withStyle("bg-th-accent-900 text-white"),
        ])
        .withStyle(`flex items-center justify-between w-full`);
    }

    return this.viewMode.getLessonContent(lesson, viewConfiguration);
  }

  getLessonComponentColor(lesson: SubstituteSchedulerLesson): ComponentColor {
    if (lesson.selectedSubstitute) {
      return "success";
    }
    return this.viewMode.getLessonComponentColor(lesson);
  }

  getLessonComponentStyle(lesson: SubstituteSchedulerLesson): string {
    let style = this.viewMode.getLessonComponentStyle(lesson);

    if (!lesson.selectedSubstitute) {
      style = style.replace(/opacity-\d+/g, "opacity-100");
    }

    return style;
  }

  /**
   * Modifies the lessons with parameters according to the selected teacher
   */
  private modifyLessons(
    lessons: SubstituteSchedulerLesson[][],
  ): SubstituteSchedulerLesson[][] {
    return lessons.map((dayLessons) =>
      dayLessons.map((lesson) => {
        const lessonClone = { ...lesson };
        // Set all lessons active where a substitute is selected, but only selectable if the substitute equals the activated in this view mode.
        lessonClone.isActive =
          !!lesson.selectedSubstitute ||
          !!lesson.teacherApplications?.some(
            (a) => a.id === this.selectedSubstitute?.id,
          );
        // Only lessons that are active can be deactivated and reactivated
        lessonClone.canActivate =
          (!lesson.selectedSubstitute && lesson.isActive) ||
          lesson.selectedSubstitute?.id === this.selectedSubstitute?.id;
        // Lessons that are active but the selected substitute is not equal to the currently selected substitute in the viewmode can not be edited
        if (
          (lesson.selectedSubstitute &&
            lesson.selectedSubstitute.id !== this.selectedSubstitute?.id) ||
          !!lesson.teacherApplications?.some(
            (a) => a.id === this.selectedSubstitute?.id,
          )
        ) {
          lessonClone.canActivate = false;
        }
        return lessonClone;
      }),
    );
  }

  private modifyReturnLessons(
    lessons: SubstituteSchedulerLesson[][],
    changedLessons: SubstituteSchedulerLesson[],
    value: boolean,
  ): SubstituteSchedulerLesson[] {
    const _changedLessons = changedLessons.filter((l) => l.canActivate);
    if (this.isDefault) {
      return _changedLessons;
    } else {
      // If a substitute is selected we do not want to modify the lessons but only add / remove a substitute if a lesson is clicked.
      // Therefore the original lesson has to be taken and add / remove a substitute to it
      return lessons
        .flat()
        .filter((l) =>
          _changedLessons.some((cl) => VacancyLesson.equals(l, cl)),
        )
        .filter((l) => l.canActivate)
        .map((l) => ({
          ...l,
          selectedSubstitute: value ? this.selectedSubstitute : undefined,
        }));
    }
  }
}
